'use client'

import React, { useState, useRef, useEffect } from 'react';
import { GameData, Question, Answer, SituationSetupData } from '@/app/types';
import QuestionItem from '@/components/SituationQuestionItem';
import { IconTarget, IconInfo, IconPlus } from '@/assets/icons/gameicon';
import { v4 as uuidv4 } from 'uuid';

const THAI_ALPHABETS = ['ก', 'ข', 'ค', 'ง', 'จ', 'ฉ', 'ช', 'ซ', 'ฌ', 'ญ', 'ฎ', 'ฏ'];
const TARGET_WIDTH = 3840;
const TARGET_HEIGHT = 1080;


interface SituationEditorProps {
  onBack?: () => void;
}

function App({ onBack }: SituationEditorProps) {
  const [gameData, setGameData] = useState<GameData>({
    title: '',
    instructions: '',
    backgroundImage: null,
    questions: [],
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const raw = window.sessionStorage.getItem('situation-setup');
    if (!raw) return;

    try {
      const setup = JSON.parse(raw) as SituationSetupData;
      setGameData((prev) => ({
        ...prev,
        title: setup.title ?? prev.title,
        instructions: setup.instructions ?? prev.instructions,
        backgroundImage: setup.backgroundImage ?? prev.backgroundImage,
      }));
    } catch (error) {
      console.error('Failed to parse setup data:', error);
    }
  }, []);

  const [activeQuestionId, setActiveQuestionId] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);

  // Function to handle image upload and resize
  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        // Create canvas for resizing
        const canvas = document.createElement('canvas');
        canvas.width = TARGET_WIDTH;
        canvas.height = TARGET_HEIGHT;
        const ctx = canvas.getContext('2d');

        if (ctx) {
          // Draw and stretch image
          ctx.drawImage(img, 0, 0, TARGET_WIDTH, TARGET_HEIGHT);
          const resizedDataUrl = canvas.toDataURL('image/jpeg', 0.9);
          setGameData(prev => ({ ...prev, backgroundImage: resizedDataUrl }));
        }
      };
      img.src = e.target?.result as string;
    };
    reader.readAsDataURL(file);
  };

  const handleImageClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!imageRef.current || !gameData.backgroundImage) return;

    const rect = imageRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;

    // Logic: If a question is active, move it. If not, create a new one.
    if (activeQuestionId) {
      setGameData(prev => ({
        ...prev,
        questions: prev.questions.map(q => q.id === activeQuestionId ? { ...q, x, y } : q)
      }));
    } else {
      const newQuestion: Question = {
        //id: crypto.randomUUID(),  
        id: uuidv4(),
        x,
        y,
        text: '',
        answers: [
          { id: uuidv4(), label: 'ก', text: '', isCorrect: false },
          { id: uuidv4(), label: 'ข', text: '', isCorrect: true },
        ]
      };

      setGameData(prev => ({
        ...prev,
        questions: [...prev.questions, newQuestion]
      }));

      setActiveQuestionId(newQuestion.id);
    }
  };

  const updateQuestion = (id: string, updates: Partial<Question>) => {
    setGameData(prev => ({
      ...prev,
      questions: prev.questions.map(q => q.id === id ? { ...q, ...updates } : q)
    }));
  };

  const deleteQuestion = (id: string) => {
    setGameData(prev => ({
      ...prev,
      questions: prev.questions.filter(q => q.id !== id)
    }));
    if (activeQuestionId === id) {
      setActiveQuestionId(null);
    }
  };

  const updateAnswer = (questionId: string, answerId: string, updates: Partial<Answer>) => {
    setGameData(prev => ({
      ...prev,
      questions: prev.questions.map(q => {
        if (q.id !== questionId) return q;
        return {
          ...q,
          answers: q.answers.map(a => a.id === answerId ? { ...a, ...updates } : a)
        };
      })
    }));
  };

  const addAnswer = (questionId: string) => {
    setGameData(prev => ({
      ...prev,
      questions: prev.questions.map(q => {
        if (q.id !== questionId) return q;
        const nextLabel = THAI_ALPHABETS[q.answers.length % THAI_ALPHABETS.length];
        const newAnswer: Answer = {
          id: uuidv4(),
          label: nextLabel,
          text: '',
          isCorrect: false
        };
        return { ...q, answers: [...q.answers, newAnswer] };
      })
    }));
  };

  const deleteAnswer = (questionId: string, answerId: string) => {
    setGameData(prev => ({
      ...prev,
      questions: prev.questions.map(q => {
        if (q.id !== questionId) return q;
        const newAnswers = q.answers.filter(a => a.id !== answerId);
        // Re-label
        const relabeledAnswers = newAnswers.map((a, idx) => ({
          ...a,
          label: THAI_ALPHABETS[idx % THAI_ALPHABETS.length]
        }));
        return { ...q, answers: relabeledAnswers };
      })
    }));
  };

  const moveQuestion = (index: number, direction: 'up' | 'down') => {
    setGameData(prev => {
      const newQuestions = [...prev.questions];
      if (direction === 'up' && index > 0) {
        // Swap with previous
        [newQuestions[index], newQuestions[index - 1]] = [newQuestions[index - 1], newQuestions[index]];
      } else if (direction === 'down' && index < newQuestions.length - 1) {
        // Swap with next
        [newQuestions[index], newQuestions[index + 1]] = [newQuestions[index + 1], newQuestions[index]];
      }
      return { ...prev, questions: newQuestions };
    });
  };

  const addQuestionManually = () => {
    const newQuestion: Question = {
      id: uuidv4(),
      x: 0.5,
      y: 0.5,
      text: '',
      answers: [
        { id: uuidv4(), label: 'ก', text: '', isCorrect: false },
        { id: uuidv4(), label: 'ข', text: '', isCorrect: true },
      ]
    };
    setGameData(prev => ({
      ...prev,
      questions: [...prev.questions, newQuestion]
    }));
    setActiveQuestionId(newQuestion.id);
  };

  const exportJson = () => {
    const dataToExport = {
      ...gameData,
      game_type_id: "2",
      owner_id: "550e8400-e29b-41d4-a716-446655440000",
      is_public: true
    }


    const dataStr = JSON.stringify(dataToExport, null, 2);
    const blob = new Blob([dataStr], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `game-${new Date().toISOString()}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleSave = async () => {

    //ส่ง api 2 ครั้ง 1 save image 2 save game
    try {
      const response = await fetch(gameData.backgroundImage);
      const blob = await response.blob();

      const formData = new FormData();
      formData.append('image', blob, "background.jpg");

      // 1 API Upload รูป
      const uploadImageResponse = await fetch(`${process.env.NEXT_PUBLIC_API_BASE_URL}/api/upload/upload-image`, {
        method: 'POST',
        body: formData,
      });


      if (!uploadImageResponse.ok) throw new Error('Upload Image failed');


      //2 API Save Game
      //ไม่เอาข้อมูลรูป เฉพาะ textData
      const { backgroundImage, ...textData } = gameData;
      const uploadResult = await uploadImageResponse.json();
      const uploadImageUrl = uploadResult.url;
      console.log("ImageUrl: ", uploadImageUrl);

      const gameDataJson = {
        ...textData,
        game_type_id: "2",
        owner_id: "550e8400-e29b-41d4-a716-446655440000",
        is_public: true,
        imageurl: uploadImageUrl,
      };

      const respondSaveGame = await fetch(`${process.env.NEXT_PUBLIC_API_BASE_URL}/api/games/save`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(gameDataJson),
      });

      if (!respondSaveGame.ok) throw new Error('Save Game failed');

    } catch (error) {
      console.error('Save error:', error);
      alert('เกิดข้อผิดพลาดในการบันทึก');
    }


  }


  return (
    <div className="w-full h-full overflow-y-auto bg-white p-8 font-sans rounded-2xl">
      <div className="max-w-6xl mx-auto space-y-6">

        {/* Header Inputs */}
        <div className="space-y-4">
          <div className="space-y-1">
            <label className="block text-sm font-bold text-gray-600">ชื่อกิจกรรม</label>
            <input
              type="text"
              className="w-full border border-gray-400 rounded p-2 focus:ring-2 focus:ring-yellow-400 focus:border-transparent outline-none"
              placeholder="ไม่มีชื่อกิจกรรม"
              value={gameData.title}
              onChange={e => setGameData({ ...gameData, title: e.target.value })}
            />
          </div>
          <div className="space-y-1">
            <label className="flex items-center gap-1 text-sm font-bold text-gray-600">
              คำแนะนำในการเล่น <IconInfo className="w-4 h-4 text-gray-400" />
            </label>
            <input
              type="text"
              className="w-full border border-gray-400 rounded p-2 focus:ring-2 focus:ring-yellow-400 focus:border-transparent outline-none text-gray-500"
              placeholder="ลากและวางคำให้ถูกต้อง"
              value={gameData.instructions}
              onChange={e => setGameData({ ...gameData, instructions: e.target.value })}
            />
          </div>
        </div>

        {/* Image Area */}
        <div className="relative border-2 border-gray-300 rounded-lg overflow-hidden bg-white select-none">
          {/* Placeholder or Image */}
          {!gameData.backgroundImage ? (
            <div className="h-64 flex flex-col items-center justify-center text-gray-400 bg-gray-50">
              <p className="mb-4">ขนาดภาพ {TARGET_WIDTH}x{TARGET_HEIGHT}</p>
              <button
                onClick={() => fileInputRef.current?.click()}
                className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-4 py-2 rounded transition-colors"
              >
                Upload Image
              </button>
            </div>
          ) : (
            <div className="relative w-full">
              {/* Visual Header inside image container */}
              <div className="absolute top-4 left-0 right-0 text-center pointer-events-none z-10">
                <span className="bg-white/80 px-3 py-1 rounded text-xs font-bold text-black shadow-sm backdrop-blur-sm">
                  ขนาดภาพ {TARGET_WIDTH}x{TARGET_HEIGHT}
                </span>
              </div>

              {/* The Interactive Image */}
              <div
                className="relative w-full cursor-crosshair group"
                onClick={handleImageClick}
              >
                {/* Maintain Aspect Ratio of 3840/1080 ~ 3.55 */}
                <img
                  ref={imageRef}
                  src={gameData.backgroundImage}
                  alt="Game Background"
                  className="w-full h-auto block"
                />

                {/* Hover Instruction */}
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity bg-black/5">
                  <span className="bg-white/90 px-4 py-2 rounded-full shadow text-sm font-medium text-gray-600">
                    {activeQuestionId ? 'กดเพื่อย้ายตำแหน่งคำถาม' : 'กดที่ภาพเพื่อเพิ่มคำถาม'}
                  </span>
                </div>

                {/* Hotspots */}
                {gameData.questions.map((q, idx) => (
                  <div
                    key={q.id}
                    className={`absolute transform -translate-x-1/2 -translate-y-1/2 flex items-center gap-1 pointer-events-none transition-transform duration-200 ${activeQuestionId === q.id ? 'scale-110 z-20' : 'z-10'}`}
                    style={{ left: `${q.x * 100}%`, top: `${q.y * 100}%` }}
                  >
                    <span className={`font-bold drop-shadow-md text-lg ${activeQuestionId === q.id ? 'text-red-600' : 'text-black'}`}>{idx + 1}</span>
                    <IconTarget className={`w-6 h-6 drop-shadow-md ${activeQuestionId === q.id ? 'text-red-600' : 'text-black'}`} />
                  </div>
                ))}
              </div>
            </div>
          )}

          <input
            type="file"
            ref={fileInputRef}
            accept="image/*"
            className="hidden"
            onChange={handleImageUpload}
          />
        </div>

        {/* Upload Button (External if needed, mainly for re-upload) */}
        {gameData.backgroundImage && (
          <div className="flex justify-end">
            <button
              onClick={() => fileInputRef.current?.click()}
              className="bg-gray-300 hover:bg-gray-400 text-gray-700 font-bold py-2 px-4 rounded text-sm transition-colors"
            >
              Upload Image
            </button>
          </div>
        )}

        {/* Questions List */}
        <div className="space-y-4">
          {gameData.questions.map((q, idx) => (
            <QuestionItem
              key={q.id}
              index={idx}
              totalQuestions={gameData.questions.length}
              question={q}
              isActive={activeQuestionId === q.id}
              onSelect={() => setActiveQuestionId(q.id)}
              onUpdate={updateQuestion}
              onDelete={deleteQuestion}
              onUpdateAnswer={updateAnswer}
              onMoveUp={() => moveQuestion(idx, 'up')}
              onMoveDown={() => moveQuestion(idx, 'down')}
              onAddAnswer={addAnswer}
              onDeleteAnswer={deleteAnswer}
            />
          ))}

          {/* Manual Add Question Button */}
          <div className="text-center py-4">
            <button
              onClick={addQuestionManually}
              className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-full transition-colors text-sm font-medium"
            >
              <IconPlus className="w-4 h-4" />
              เพิ่มคำถาม
            </button>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="fixed bottom-8 right-8 flex gap-4 z-30">
          <button
            onClick={exportJson}
            className="bg-yellow-200 hover:bg-yellow-300 text-yellow-900 px-6 py-2 rounded-lg font-bold shadow-sm transition-colors"
          >
            Export Json
          </button>
          <button
            onClick={() => {
              setActiveQuestionId(null);
              handleSave();
            }}


            className="bg-yellow-300 hover:bg-yellow-400 text-yellow-900 px-6 py-2 rounded-lg font-bold shadow-lg transition-colors"
          >
            บันทึก
          </button>
        </div>

      </div>

      {/* Back Button */}
      <button
        onClick={onBack}
        className="fixed bottom-8 left-8 px-6 py-2.5 bg-white border border-gray-200 text-gray-600 rounded-lg font-medium hover:bg-gray-50 shadow-sm z-30"
      >
        Back
      </button>
    </div>
  );
}

export default App;
